/**
 * Branding dinámico por subdominio.
 * En el load del app, detecta el subdominio (ej: robusta.redesip.org → "robusta"),
 * mapea a una sucursalId, pide el branding al API y actualiza:
 *  - <link rel="icon"> (favicon)
 *  - <link rel="apple-touch-icon"> (PWA iOS)
 *  - <link rel="manifest"> (PWA Android — usa endpoint dinámico)
 *  - <meta name="theme-color">
 *  - <title>
 */

// Mapeo subdominio → sucursalId
// (configurable acá hasta que lo movamos a una tabla)
const SUBDOMINIO_A_SUCURSAL: Record<string, number> = {
  // Robusta = grupo (Francelina + Attimo) — sucursalId=1 (Francelina como cabecera)
  "robusta": 1,
  "francelina": 1,
  "attimo": 2,
  // Bowling
  "bowling": 1,            // bowling DB usa sucursalId=1
  "elevation": 1,
  "velapark": 1,
};

interface Branding {
  sucursalId: number | null;
  nombre: string;
  logoUrl: string | null;
  colorPrimario: string;
  colorSecundario: string | null;
}

function detectarSucursal(): number | undefined {
  const host = window.location.hostname.toLowerCase();
  // Tomar primera parte antes del primer punto
  const sub = host.split(".")[0];
  return SUBDOMINIO_A_SUCURSAL[sub];
}

function setFavicon(url: string) {
  let link = document.getElementById("favicon") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = "favicon";
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
  // El tipo se infiere desde la URL
  if (/\.svg$/i.test(url)) link.type = "image/svg+xml";
  else if (/\.png$/i.test(url)) link.type = "image/png";
  else if (/\.ico$/i.test(url)) link.type = "image/x-icon";
  else link.removeAttribute("type");
}

function setAppleTouch(url: string) {
  // Reemplazar todos los apple-touch-icon existentes
  document.querySelectorAll('link[rel="apple-touch-icon"]').forEach((el) => {
    (el as HTMLLinkElement).href = url;
  });
}

function setManifest(url: string) {
  let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  link.href = url;
}

function setThemeColor(color: string) {
  let meta = document.getElementById("theme-color") as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    meta.id = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = color;
}

function setTitle(nombre: string) {
  document.title = nombre || "CRM Redesip";
  // apple-mobile-web-app-title también (para el ícono de iOS)
  let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "apple-mobile-web-app-title";
    document.head.appendChild(meta);
  }
  meta.content = (nombre || "CRM").slice(0, 12);
}

export async function inicializarBranding(): Promise<void> {
  const sid = detectarSucursal();
  const qs = sid ? `?sucursal=${sid}` : "";
  try {
    const res = await fetch(`/api/v1/branding${qs}`);
    if (!res.ok) return;
    const b: Branding = await res.json();

    // Carrera entre esta carga generica (por subdominio) y la de la pagina
    // especifica del usuario logeado (aplicarTituloPagina/aplicarFaviconPagina,
    // ver mas abajo) -- si la pagina ya aplico la sucursal real del usuario
    // mientras este fetch seguia en vuelo, no pisarla con el default generico.
    if (paginaOverrideAplicado) return;

    if (b.logoUrl) {
      setFavicon(b.logoUrl);
      setAppleTouch(b.logoUrl);
    }
    setManifest(`/api/v1/branding/manifest.json${qs}`);
    if (b.colorPrimario) setThemeColor(b.colorPrimario);
    setTitle(b.nombre);

    // Cache local para evitar el flicker en próximas cargas
    try {
      localStorage.setItem("branding_cache", JSON.stringify(b));
    } catch { /* ignore */ }
  } catch {
    // Si falla, intentar usar cache previo
    if (paginaOverrideAplicado) return;
    try {
      const cached = localStorage.getItem("branding_cache");
      if (cached) {
        const b: Branding = JSON.parse(cached);
        if (b.logoUrl) {
          setFavicon(b.logoUrl);
          setAppleTouch(b.logoUrl);
        }
        if (b.colorPrimario) setThemeColor(b.colorPrimario);
        setTitle(b.nombre);
      }
    } catch { /* ignore */ }
  }
}

/**
 * Favicon dinámico por página: a diferencia de inicializarBranding() (que
 * corre UNA vez al cargar la app según el subdominio), esto lo llaman
 * páginas que ya saben con certeza el logo de la sucursal/encuesta
 * específica que están mostrando (menú digital, encuestas, reservas) -- para
 * que el ícono de la pestaña sea el de ESA sucursal puntual, no el genérico
 * del subdominio completo.
 */
let paginaOverrideAplicado = false;

export function aplicarFaviconPagina(logoUrl: string | null | undefined): void {
  if (!logoUrl) return;
  paginaOverrideAplicado = true;
  setFavicon(logoUrl);
  setAppleTouch(logoUrl);
}

/**
 * Título de pestaña específico de página — hermano de aplicarFaviconPagina().
 * Sin esto, el <title> se queda con el que puso inicializarBranding() al
 * cargar la app (el del subdominio/grupo), aunque la página esté mostrando
 * una sucursal puntual distinta (ej. menú de Attimo dentro del subdominio
 * de Robusta, cuya cabecera es Francelina).
 */
export function aplicarTituloPagina(nombre: string | null | undefined): void {
  if (!nombre) return;
  paginaOverrideAplicado = true;
  setTitle(nombre);
}

/** Aplicar branding desde cache inmediatamente (sin esperar el fetch) */
export function aplicarBrandingCacheado(): void {
  try {
    const cached = localStorage.getItem("branding_cache");
    if (!cached) return;
    const b: Branding = JSON.parse(cached);
    if (b.logoUrl) {
      setFavicon(b.logoUrl);
      setAppleTouch(b.logoUrl);
    }
    if (b.colorPrimario) setThemeColor(b.colorPrimario);
    setTitle(b.nombre);
  } catch { /* ignore */ }
}
